import React, { ReactNode, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import PlantCatalog from './components/PlantCatalog';
import MyPlants from './components/MyPlants';
import Tasks from './components/Tasks';
import Calendar from './components/Calendar';
import Weather from './components/Weather';
import Navbar from './components/Navbar';
import LandingPage from './components/LandingPage';
import AboutUs from './components/AboutUs';
import Features from './components/Features';
import Pricing from './components/Pricing';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import ComingSoonPage from './components/ComingSoonPage';
import api from './config/api';

// Protected Route Component
interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Public Route Component (redirects to dashboard if already logged in)
interface PublicRouteProps {
  children: ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/dashboard" /> : <>{children}</>;
};

// App Content Component (needs to be inside Router for useLocation to work)
const AppContent: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check for bypass parameter
  const urlParams = new URLSearchParams(window.location.search);
  const bypassComingSoon = urlParams.get('preview') === 'true' || 
                          urlParams.get('bypass') === 'admin2024' ||
                          localStorage.getItem('bypassComingSoon') === 'true';

  useEffect(() => {
    // Update document title based on current language
    const appName = t('branding.appName');
    document.title = `${appName} - ${t('branding.tagline')}`;
  }, [t, i18n.language]);

  useEffect(() => {
    // If bypass parameter exists, save it to localStorage
    if (urlParams.get('bypass') === 'admin2024') {
      localStorage.setItem('bypassComingSoon', 'true');
      console.log('🔓 Coming Soon bypass activated via URL parameter');
    }
    
    checkSettings();
    // Check every 60 seconds for changes
    const interval = setInterval(checkSettings, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      console.log('App settings check:', response.data);
      setIsComingSoon(response.data.comingSoonMode === true);
    } catch (error) {
      console.error('Failed to check settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Kraunama...</div>
      </div>
    );
  }

  // Don't show Coming Soon for admin routes
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  
  // Add bypass indicator for testing
  if (bypassComingSoon && isComingSoon) {
    console.log('⚠️ Coming Soon BYPASSED - Testing Mode');
  }
  
  // Show Coming Soon ONLY if:
  // - Coming Soon is enabled
  // - NOT on admin route
  // - NOT bypassed
  if (isComingSoon && !isAdminRoute && !bypassComingSoon) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-green-100 flex items-center justify-center">
        <div className="text-center p-8 max-w-2xl">
          <div className="mb-8">
            <div className="text-6xl mb-4">🌱</div>
            <h1 className="text-5xl font-bold text-green-800 mb-4">
              Kiemo Draugas
            </h1>
          </div>
          <h2 className="text-3xl text-gray-700 mb-6">
            Jau greitai!
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Kuriame puikią platformą jūsų kiemo priežiūrai. 
            Sugrįšime netrukus su naujomis funkcijomis!
          </p>
          <div className="mt-8 p-4 bg-white rounded-lg shadow-md">
            <p className="text-gray-600 mb-2">Turite klausimų?</p>
            <p className="text-lg text-green-600">📧 info@kiemodraugas.lt</p>
          </div>
        </div>
      </div>
    );
  }

  // Hide navbar on login, register, and all dashboard-related pages (landing and about us use shared navbar)
  const shouldShowNavbar = !['/', '/login', '/register', '/dashboard', '/plants', '/my-plants', '/tasks', '/calendar', '/weather', '/apie-mus', '/funkcijos', '/kainos', '/admin/login', '/admin/dashboard'].includes(location.pathname);

  return (
    <div className="App">
      {/* Bypass indicator */}
      {bypassComingSoon && isComingSoon && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black text-center py-2 text-sm font-semibold z-50">
          🔓 BYPASS ACTIVE - Coming Soon mode bypassed for testing
        </div>
      )}
      {shouldShowNavbar && <Navbar />}
      <main className="main-content">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/funkcijos" element={<Features />} />
          <Route path="/kainos" element={<Pricing />} />
          <Route path="/apie-mus" element={<AboutUs language={i18n.language} />} />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/plants" 
            element={
              <ProtectedRoute>
                <PlantCatalog />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/my-plants" 
            element={
              <ProtectedRoute>
                <MyPlants />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tasks" 
            element={
              <ProtectedRoute>
                <Tasks />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/calendar" 
            element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            } 
          />
                    <Route 
            path="/weather" 
            element={
              <ProtectedRoute>
                <Weather />
              </ProtectedRoute>
            }
          />
          
          {/* Admin Routes */}
          <Route 
            path="/admin/login" 
            element={<AdminLogin />} 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            } 
          />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App; 